import { ReactNode, useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useChurchBranding } from '@/hooks/useChurchBranding';
import Logo from '@/components/Logo';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import Breadcrumb from '@/components/Breadcrumb';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import { LogOut, Search, Command, Palette, Bell, Home } from 'lucide-react';
import { buildPrimaryNav, getChurchNavItems, PERSONAL_NAV, PLATFORM_NAV } from '@/config/navigation';
import { Link, useLocation } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { GlobalCommandPalette } from '@/components/admin/GlobalCommandPalette';
import { ThemeCustomizer } from '@/components/admin/ThemeCustomizer';
import { AccessibilityHelpers, useAccessibility } from '@/components/admin/AccessibilityHelpers';
import { ChurchSwitcher } from '@/components/admin/ChurchSwitcher';
import { NotificationsCenter } from '@/components/admin/NotificationsCenter';
import { QuickActionsDrawer } from '@/components/admin/QuickActionsDrawer';
import { PerformanceMonitor } from '@/components/admin/PerformanceMonitor';
import { useNotificationsFeed } from '@/hooks/useNotificationsFeed';

interface LayoutProps {
  children: ReactNode;
}

// Super-admin-only header tooling and overlays, extracted so its hooks (notably
// useNotificationsFeed, which polls audit logs) only run for super admins. This
// keeps the single shell usable by members without firing admin-only queries,
// and preserves rules-of-hooks (the component is conditionally rendered, not the
// hooks within it).
const SuperAdminTools = () => {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const { unreadCount } = useNotificationsFeed();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Shift + A → Quick Actions
      if (e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setQuickActionsOpen(true);
      }
      // Ctrl/Cmd + Shift + N → Notifications (metaKey so it works on macOS too)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        setNotificationsOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setNotificationsOpen(true)}
        className="relative min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 p-2"
        title="Notifications"
      >
        <Bell className="h-4 w-4 md:h-5 md:w-5" />
        {unreadCount > 0 && (
          <Badge
            variant="destructive"
            className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 text-[10px]"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
        <span className="sr-only">Notifications</span>
      </Button>
      <NotificationsCenter open={notificationsOpen} onOpenChange={setNotificationsOpen} />
      <QuickActionsDrawer open={quickActionsOpen} onOpenChange={setQuickActionsOpen} />
      <PerformanceMonitor />
    </>
  );
};

const Layout = ({ children }: LayoutProps) => {
  const { signOut, user } = useAuth();
  const { isSuperAdmin, isChurchAdmin, isMember } = usePermissions();
  const { church } = useChurchBranding();
  const location = useLocation();

  // Admins are inherently approved; only unapproved members are routed to the
  // waiting-approval holding page when they click a nav item.
  const canNavigate = isMember || isChurchAdmin;

  // State for modals/drawers
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [themeCustomizerOpen, setThemeCustomizerOpen] = useState(false);
  
  // Accessibility helpers
  const { showHelp, setShowHelp } = useAccessibility();

  // Navigation comes from the single source of truth (config/navigation), keyed
  // off the permission source of truth. No bespoke per-shell menu definitions.
  //
  // When drilling into a specific church (/church/:churchId/*), the sidebar
  // switches to that church's scoped modules so clicks stay in-context (this
  // replaces the old AdminLayout church tree). A leading Dashboard link always
  // provides a way back to the main shell; super admins also keep the Platform
  // group.
  const scopedChurchId = location.pathname.match(/^\/church\/([^/]+)/)?.[1];
  const navigationGroups = scopedChurchId
    ? [
        { label: 'Home', items: [{ name: 'Dashboard', href: '/', icon: Home }] },
        { label: 'Managing Church', items: getChurchNavItems(scopedChurchId) },
        ...(isSuperAdmin ? [PLATFORM_NAV] : []),
      ]
    : buildPrimaryNav({ isSuperAdmin, isChurchAdmin });

  const isActive = (href: string) => {
    // Index-style routes match exactly, so a child route (e.g. /churches/new)
    // doesn't keep the parent list item (/churches) highlighted.
    if (href === '/' || href === '/churches') {
      return location.pathname === href;
    }
    // Church overview (/church/:id) must not stay active on its sub-routes.
    if (/^\/church\/[^/]+$/.test(href)) {
      return location.pathname === href;
    }
    // Segment-boundary prefix match (so /members doesn't match /members-foo).
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // Get user initials for avatar
  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return user?.email?.[0]?.toUpperCase() || 'U';
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K for Command Palette
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
      
      // Cmd/Ctrl + / for Accessibility Help
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setShowHelp(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setShowHelp]);

  return (
    <SidebarProvider>
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>
      <div className="min-h-screen w-full flex bg-background">
        {/* App Sidebar */}
        <Sidebar className="border-r">
          {/* Sidebar Header */}
          <SidebarHeader className="border-b">
            <div className="flex items-center gap-2 px-4 py-2">
              {church?.logo_url ? (
                <img
                  src={church.logo_url}
                  alt={`${church.name} logo`}
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <Logo size="sm" />
              )}
              {isSuperAdmin && (
                <span className="ml-2 px-2 py-1 text-xs bg-primary/10 text-primary rounded-full">
                  Admin
                </span>
              )}
            </div>
            <div className="px-4 pb-2">
              <p className="text-sm text-muted-foreground">
                {isSuperAdmin 
                  ? 'Super Admin Dashboard' 
                  : church?.name || `Welcome, ${user?.user_metadata?.first_name || 'Member'}`
                }
              </p>
            </div>
          </SidebarHeader>

          {/* Sidebar Content */}
          <SidebarContent className='no-scrollbar'>
            {navigationGroups.map((group, groupIndex) => (
              <SidebarGroup key={group.label}>
                <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {group.label}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <SidebarMenuItem key={item.name}>
                          <SidebarMenuButton 
                            asChild
                            isActive={isActive(item.href)}
                            className="w-full min-h-[44px] md:min-h-0"
                          >
                            <Link to={canNavigate ? item.href : '/waiting-approval'}>
                              <Icon className="w-4 h-4" />
                              <span>{item.name}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
                {groupIndex < navigationGroups.length - 1 && <SidebarSeparator />}
              </SidebarGroup>
            ))}
            
            {/* Personal section at bottom - only show for non-super-admin */}
            {!isSuperAdmin && (
              <SidebarGroup>
                <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Personal
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {PERSONAL_NAV.map((item) => {
                      const Icon = item.icon;
                      return (
                        <SidebarMenuItem key={item.href}>
                          <SidebarMenuButton
                            asChild
                            isActive={isActive(item.href)}
                            className="w-full min-h-[44px] md:min-h-0"
                          >
                            <Link to={canNavigate ? item.href : '/waiting-approval'}>
                              <Icon className="w-4 h-4" />
                              <span>{item.name}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </SidebarContent>

          {/* Sidebar Footer */}
          <SidebarFooter className="border-t">
            <SidebarMenu>
              {/* User info display */}
              <SidebarMenuItem>
                <div className="flex items-center gap-3 px-3 py-2 border-b">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {getInitials(user?.user_metadata?.first_name, user?.user_metadata?.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {user?.user_metadata?.first_name && user?.user_metadata?.last_name
                        ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
                        : user?.email?.split('@')[0]}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton 
                  onClick={signOut} 
                  className="w-full text-left min-h-[44px] md:min-h-0"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Sign Out</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>

        {/* Main content with mobile header */}
        <SidebarInset className="flex flex-col">
          {/* Mobile/Desktop Header with Search and Controls */}
          <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex items-center justify-between w-full gap-2 px-3">
              {/* Left side - Menu trigger and logo */}
              <div className="flex items-center gap-2">
                <SidebarTrigger className="min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0" />
                <div className="flex items-center gap-2 lg:hidden">
                  {church?.logo_url ? (
                    <img
                      src={church.logo_url}
                      alt={`${church.name} logo`}
                      className="h-8 w-8 object-contain"
                    />
                  ) : (
                    <Logo size="sm" />
                  )}
                  {isSuperAdmin && (
                    <span className="ml-2 px-2 py-1 text-xs bg-primary/10 text-primary rounded-full">
                      Admin
                    </span>
                  )}
                </div>
              </div>

              {/* Right side - Search and controls */}
              <div className="flex items-center gap-1 md:gap-2">
                {/* Desktop Search */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCommandPaletteOpen(true)}
                  className="hidden lg:flex items-center gap-2 min-w-[200px] justify-start text-muted-foreground min-h-[36px]"
                >
                  <Search className="h-4 w-4" />
                  <span className="text-sm">Search...</span>
                  <div className="ml-auto flex items-center gap-1">
                    <Command className="h-3 w-3" />
                    <span className="text-xs">K</span>
                  </div>
                </Button>

                {/* Mobile Search Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCommandPaletteOpen(true)}
                  className="lg:hidden min-h-[44px] min-w-[44px] p-2"
                  title="Search"
                >
                  <Search className="h-5 w-5" />
                  <span className="sr-only">Search</span>
                </Button>

                {/* Church switcher — multi-church admins + super admins.
                    (ChurchSwitcher self-hides for single-church, non-super users.) */}
                {isChurchAdmin && <ChurchSwitcher />}

                {/* Super-admin tooling: notifications bell + quick actions */}
                {isSuperAdmin && <SuperAdminTools />}

                {/* Theme Customizer */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setThemeCustomizerOpen(true)}
                  className="min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 p-2"
                  title="Customize Theme"
                >
                  <Palette className="h-4 w-4 md:h-5 md:w-5" />
                  <span className="sr-only">Customize Theme</span>
                </Button>

                {/* User Avatar - Desktop only */}
                <div className="hidden md:flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {getInitials(user?.user_metadata?.first_name, user?.user_metadata?.last_name)}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </div>
            </div>
          </header>

          <main id="main-content" className="flex-1 focus:outline-none" tabIndex={-1}>
            <Breadcrumb isAdmin={isSuperAdmin} />
            {children}
          </main>
          <Footer />
        </SidebarInset>
      </div>

      {/* Global Command Palette */}
      <GlobalCommandPalette 
        open={commandPaletteOpen} 
        onOpenChange={setCommandPaletteOpen} 
      />

      {/* Theme Customizer */}
      <ThemeCustomizer 
        open={themeCustomizerOpen} 
        onOpenChange={setThemeCustomizerOpen} 
      />

      {/* Accessibility Help */}
      <AccessibilityHelpers 
        open={showHelp} 
        onOpenChange={setShowHelp} 
      />

      {/* ARIA Live Region for announcements */}
      <div 
        aria-live="polite" 
        aria-atomic="true" 
        className="sr-only" 
        id="live-region"
        role="status"
      />
    </SidebarProvider>
  );
};

export default Layout;